/**
 * MessageBridge - Handles postMessage communication with the iframe
 */

import { EmbedMessage, MESSAGE_TYPES, PayChargeFormLayout, PayChargeLabels } from './types';

export type MessageHandler = (type: string, payload: unknown) => void;

export class MessageBridge {
  private iframeOrigin: string;
  private iframeWindow: Window | null = null;
  private handler: MessageHandler | null = null;
  private boundListener: ((event: MessageEvent) => void) | null = null;

  constructor(iframeUrl: string) {
    // Extract origin from iframe URL
    try {
      const url = new URL(iframeUrl, window.location.origin);
      this.iframeOrigin = url.origin;
    } catch {
      // If URL parsing fails, assume same origin
      this.iframeOrigin = window.location.origin;
    }
  }

  /**
   * Set the iframe window for sending messages
   */
  setIframeWindow(iframeWindow: Window): void {
    this.iframeWindow = iframeWindow;
  }

  /**
   * Start listening for messages from the iframe
   */
  listen(handler: MessageHandler): void {
    this.handler = handler;

    this.boundListener = (event: MessageEvent) => {
      // Accept only messages from the iframe's own origin. The previous form was
      //   event.origin !== iframeOrigin && iframeOrigin !== window.location.origin
      // whose second conjunct is false for a same-origin iframe, so the whole check
      // was skipped and any origin was trusted (ISSUE-1084 round 3 finding 2).
      if (event.origin !== this.iframeOrigin) {
        console.warn('[PayChargeEmbed] Message from unauthorized origin:', event.origin);
        return;
      }

      // Validate message structure. An origin check alone is not sufficient:
      // the payload is passed to application handlers, so it must be shaped.
      const data = event.data;
      if (!data || typeof data !== 'object' || typeof data.type !== 'string') {
        return;
      }

      // Only handle our messages (pce: prefix)
      if (!data.type.startsWith('pce:')) {
        return;
      }

      if (data.payload !== undefined && typeof data.payload !== 'object') {
        console.warn('[PayChargeEmbed] Ignoring message with a non-object payload:', data.type);
        return;
      }

      // Pass to handler
      this.handler?.(data.type, data.payload);
    };

    window.addEventListener('message', this.boundListener);
  }

  /**
   * Send a message to the iframe
   */
  send<T>(type: string, payload: T): void {
    if (!this.iframeWindow) {
      console.warn('[PayChargeEmbed] Cannot send message: iframe not ready');
      return;
    }

    const message: EmbedMessage<T> = {
      type,
      payload,
      timestamp: Date.now(),
    };

    this.iframeWindow.postMessage(message, this.iframeOrigin);
  }

  /**
   * Send configuration to the iframe
   */
  sendConfig(config: {
    sessionToken: string;
    apiUrl: string;
    amount?: number;
    currency?: string;
    labels?: PayChargeLabels;
    formLayout?: PayChargeFormLayout;
    parentOrigin: string;
  }): void {
    this.send(MESSAGE_TYPES.CONFIGURE, config);
  }

  /**
   * Send amount update to the iframe
   */
  sendAmountUpdate(amount: number): void {
    this.send(MESSAGE_TYPES.SET_AMOUNT, { amount });
  }

  /**
   * Stop listening and clean up
   */
  destroy(): void {
    if (this.boundListener) {
      window.removeEventListener('message', this.boundListener);
      this.boundListener = null;
    }
    this.handler = null;
    this.iframeWindow = null;
  }
}
